import {FocusTrap} from 'focus-trap-react';
import PropTypes from 'prop-types';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useSelector, useStore} from 'react-redux';
import {Button} from '~library/atoms/Button';
import {Divider} from '~library/atoms/Divider';
import {Typography} from '~library/atoms/Typography';
import {CheckmarkIcon} from '~library/svg/CheckmarkIcon';
import {CloseLineIcon} from '~library/svg/CloseLineIcon';
import {
  WTB_RESET_SELECTIONS,
  WTB_TOGGLE_EDITING,
  WTB_UPDATE_SELECTIONS,
} from '../reducers/constants';
import {EditSelection} from './WhatToBringEdit';

import './WhatToBring.sass';

const WtbOverlaySection = ({
  item,
  userId,
  isHost,
  hasRsvpd,
  items,
  selections,
  store,
  handleEditBtn,
}) => {
  const [selected, setSelected] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: should we specify item.id here?
  useEffect(() => {
    setSelected(selections.some((obj) => obj.id === item.id));
  }, [selections]);

  const handleSelected = useCallback(() => {
    const matchingSelection = items.find((i) => i.id === item.id);
    const quantity =
      matchingSelection.selections.find((sel) => sel.user.id === userId)?.quantity || 1;

    store.dispatch({
      type: WTB_UPDATE_SELECTIONS,
      payload: {
        type: selected ? 'remove' : 'add',
        item: {...item, selectedQuantity: quantity},
      },
    });
  }, [items, store, item, userId, selected]);

  return (
    <div className="wtb-overlay__section" key={`item_${item.id}`}>
      <div className="wtb-overlay__section-title-wrapper">
        <Typography variant="header3" className="wtb-overlay__paragraph">
          {item.name}
        </Typography>
      </div>
      <Typography
        variant="label2"
        className={`wtb-overlay__section-remaining small ${item.remaining < 1 ? 'disabled' : ''}`}
      >
        {item.quantity - item.remaining} of {item.quantity} FILLED
      </Typography>
      {item.selections.map((p) => (
        <div
          className="wtb-overlay__signed-up-item"
          data-qa-id={`signedUpListing_${p.user.id}`}
          key={`item_${item.id}_selection_${p.user.id}`}
        >
          <Typography variant="paragraph2" className="wtb-overlay__selection-user-name">
            {p.user.first_name}
          </Typography>
          {p.user.id === userId && (
            <button
              type="button"
              className="unstyled-button link small"
              data-qa-id="wtb-remove"
              onClick={() => handleEditBtn(item, p.quantity)}
            >
              Edit
            </button>
          )}
          <Typography variant="paragraph2" className="small">
            {p.quantity}
          </Typography>
        </div>
      ))}
      {item.remaining > 0 &&
        (isHost || hasRsvpd ? (
          <Button
            data-qa-id="sign-me-up"
            className={`wtb-overlay__section__sign-up-btn${selected ? ' selected' : ''}`}
            onClick={handleSelected}
            size="small"
            variant="secondary"
            data-impression-tracking="true"
          >
            {selected ? (
              <>
                <CheckmarkIcon ratio={0.5} /> Selected
              </>
            ) : (
              `Sign Me Up`
            )}
          </Button>
        ) : (
          <Button
            data-qa-id="rsvp-to-contribute"
            disabled
            size="large"
            variant="primary"
            data-impression-tracking="true"
          >
            RSVP to Contribute
          </Button>
        ))}
    </div>
  );
};

export const WhatToBringOverlay = ({
  open,
  onClose,
  userId,
  onAssign,
  name,
  isHost,
  hasRsvpd,
  onUnassign,
  items,
}) => {
  const store = useStore();
  const [loading, setLoading] = useState(true);
  const {pendingSelections, isEditing} = useSelector((state) => state.whatToBring);

  useEffect(() => {
    setLoading(items.length === 0);
  }, [items]);

  const mySignups = items.filter((item) =>
    item.selections.some((selection) => selection.user.id === userId)
  );

  useEffect(() => {
    if (open) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }
  }, [open]);

  const handleEditBtn = (item, quantity) => {
    store.dispatch({
      type: WTB_UPDATE_SELECTIONS,
      payload: {
        type: 'replace',
        item: {
          ...item,
          selectedQuantity: quantity,
        },
      },
    });
  };

  const seeMySignups = () => {
    mySignups.forEach((signup) => {
      const quantity = signup.selections.find((sel) => sel.user.id === userId)?.quantity || 1;

      store.dispatch({
        type: WTB_UPDATE_SELECTIONS,
        payload: {
          type: 'add',
          item: {...signup, selectedQuantity: quantity},
        },
      });
    });

    store.dispatch({
      type: WTB_TOGGLE_EDITING,
    });
  };

  const sortedItems = items.sort((a, b) => {
    if (a.remaining === 0 && b.remaining !== 0) {
      return 1; // Move 'a' to the end
    }
    if (a.remaining !== 0 && b.remaining === 0) {
      return -1; // Move 'b' to the end
    }
    return a.remaining - b.remaining; // Compare by value
  }); // Sort filled items at the end

  const listRef = useRef(null);
  const editorRef = useRef(null);

  return (
    /** TODO: Use ResponsivePanel when we update View Invitation */
    <>
      {/** biome-ignore lint/a11y/noStaticElementInteractions: existing linting error */}
      {/** biome-ignore lint/a11y/useKeyWithClickEvents: existing linting error */}
      <div className={`view-invitation__overlay-bg ${open ? 'open' : ''}`} onClick={onClose} />
      <div className={`view-invitation__wtb-overlay ${open ? 'open' : ''}`}>
        <FocusTrap
          active={open}
          containerElements={isEditing ? [editorRef.current] : [listRef.current]}
        >
          <div className={`wtb-overlay__content${isEditing ? ' edit-selections' : ''}`}>
            <div className="wtb-overlay__list-page" ref={listRef}>
              <div className="wtb-overlay__body">
                <div className="wtb-overlay__heading">
                  <Typography
                    className="wtb-overlay__title"
                    variant="paragraph2"
                    data-qa-id="what-to-bring-overlay-header"
                  >
                    What to Bring List
                  </Typography>
                  {/** biome-ignore lint/a11y/useButtonType: existing linting error */}
                  <button
                    data-qa-id="what-to-bring-close"
                    className="unstyled-button wtb-overlay__close-btn"
                    onClick={onClose}
                    aria-label="close modal"
                  >
                    <CloseLineIcon />
                  </button>
                </div>
                <Divider marginBottom="1.5rem" />
                {loading ? (
                  <div className="wtb-overlay__loading">
                    <span className="evt-spinner" />
                    <Typography variant="paragraph2">Loading What to Bring...</Typography>
                  </div>
                ) : (
                  <>
                    {mySignups.length > 0 && (
                      <Button
                        data-qa-id="wtb-see-my-sign-ups"
                        className="wtb-overlay__section__see-sign-ups-btn"
                        onClick={() => seeMySignups()}
                        size="small"
                        variant="primary"
                      >
                        See My Sign Ups
                      </Button>
                    )}
                    {sortedItems.map((i, key) => (
                      <WtbOverlaySection
                        item={i}
                        items={items}
                        store={store}
                        selections={pendingSelections}
                        key={i.id}
                        name={name}
                        userId={userId}
                        isHost={isHost}
                        hasRsvpd={hasRsvpd}
                        handleEditBtn={handleEditBtn}
                      />
                    ))}
                  </>
                )}
              </div>
              {pendingSelections.length > 0 && (
                <div className="wtb-overlay__footer">
                  <Button
                    variant="primary"
                    size="large"
                    className="wtb-overlay__continue-with-selected"
                    onClick={() => {
                      store.dispatch({
                        type: WTB_TOGGLE_EDITING,
                      });
                    }}
                    fullWidth
                    data-qa-id="continue-with-selected-btn"
                  >
                    {`Continue (${pendingSelections.length} selected)`}
                  </Button>
                </div>
              )}
            </div>
            <EditSelection
              backToList={() => {
                store.dispatch({
                  type: WTB_RESET_SELECTIONS,
                });
              }}
              isEditing={isEditing}
              items={items}
              pendingSelections={pendingSelections}
              onUnassign={onUnassign}
              onAssign={onAssign}
              store={store}
              ref={editorRef}
            />
          </div>
        </FocusTrap>
      </div>
    </>
  );
};

WhatToBringOverlay.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      quantity: PropTypes.number.isRequired,
      remaining: PropTypes.number.isRequired,
      selections: PropTypes.arrayOf(
        PropTypes.shape({
          quantity: PropTypes.number.isRequired,
          user: PropTypes.shape({
            // biome-ignore lint/style/useNamingConvention: camel case
            first_name: PropTypes.string.isRequired,
            id: PropTypes.string.isRequired,
            // biome-ignore lint/style/useNamingConvention: camel case
            last_name: PropTypes.string.isRequired,
          }),
        })
      ),
    })
  ).isRequired,
  onAssign: PropTypes.func,
  onUnassign: PropTypes.func,
  userId: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  isHost: PropTypes.bool,
  hasRsvpd: PropTypes.bool,
};
